import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Col, Row} from "reactstrap";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ArrowBack, FileCopyOutlined} from "@mui/icons-material";
import Api from "../../../../../../../core/services/api/api";
import {formatMoney, stringComparator} from "../../../../../../../core/services/utils/utils";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import OrderInformationViewNotesSection, {OrderNoteAutoDefineTypes} from "../../../../../../components/app-specific/order-inforamtion-shared-sections/notes";
import OrderInformationViewShippingInfoCard from "../../../../../../components/app-specific/order-inforamtion-shared-sections/shipping-info-card/shipping-information";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import SummaryInfoCard from "../../../../../../components/app-specific/summary-cards/summary-info-card";
import CreditInformationViewBillInfoSection from "./sections/bill-info";
import CreditInformationViewProductsSection from "./sections/products";
import {Box, SvgIcon, Tooltip, Typography} from "@mui/material";
import {ReactComponent as Printer} from '../../../../../../../assets/images/printer.svg'
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import useCachedPriceLevelsDictionaryController from "../../../../../../hooks/use-price-level-dictionary";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {Link, useParams} from "react-router-dom";
import useQuery from "../../../../../../hooks/use-query";
import {useAppSelector} from "../../../../../../redux";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import PrintOrderConfigurationsDialog from "../../../../../../components/dialogs/print-order-configurations";
import Button from "../../../../../../components/base/button/button";
import {Popover} from "../../../../../../components/app-specific/popover";
import {stringifyUrl} from "query-string";
import DuplicateOrderDialog, {DuplicateOrderVariants} from "../../../../../../components/dialogs/duplicate-order-dialog";

const tabs = {
    products: 'products',
    notes: 'notes',
}

const orderInfoSummaryBodyIds = {
    transferCode: 'transferCode',
    orderNo: 'orderNo',
    salesPerson: 'salesPerson',
    accountManager: 'accountManager',
    territory: 'territory',
    poNo: 'poNo',
    termsCode: 'termsCode',
    orderDate: 'orderDate',
    invoiceDate: 'invoiceDate',
    requiredDate: 'requiredDate',
    referenceNo: 'referenceNo',
}

const userInfoSummaryBodyIds = {
    accountReceivable: 'accountReceivable',
    limitedCredit: 'limitedCredit',
    otherOrders: 'otherOrders',
    availableCredit: 'availableCredit',
    priceLevel: 'priceLevel',
    paymentTerms: 'paymentTerms',
}

const defaultTab = tabs.products
const initPrintOrderConfigurationsDialogState = {open: false, printUrl: '', onClose: () => void 0};

const CreditInformationView = () => {
    const params = useParams();
    const [query, setQuery] = useQuery();
    const [loading, setLoading] = useState(true);
    const [order, _setOrder] = useState({});
    const [accountInfoLoading, setAccountInfoLoading] = useState(true);
    const [accountInfo, setAccountInfo] = useState({});
    const [printOrderConfigurationsDialog, setPrintOrderConfigurationsDialog] = useState(initPrintOrderConfigurationsDialogState);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [shippingInfo, setShippingInfo] = useState({});
    const [freeShippingEligibility, setFreeShippingEligibility] = useState({});
    const [freightEstimation, setFreightEstimation] = useState(null);
    const [loadingFreightEstimation, setLoadingFreightEstimation] = useState(false);
    const [loadingShippingInfo, setLoadingShippingInfo] = useState(true);
    const isMounted = useIsMounted();
    const freeFreightRequestIdRef = useRef(null);
    const shippingInfoCompanyIdRef = useRef(null);
    const salesOrderIdRef = useRef(null);

    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const {configureNotebookTags} = useConfigureNotebook('credit-information-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();
    const operatorAccessLevels = useAccessLevels();

    const tabsList = useMemo(() => [
        {label: "Products", value: tabs.products},
        {label: "Notes", value: tabs.notes},
    ], [])

    const orderId = useMemo(() => Number(params.orderId), [params.orderId])
    const customerId = useMemo(() => Number(params.customerId), [params.customerId])

    const tab = useMemo(() => {
        let selected = query?.tab;
        if (!selected || !tabsList?.map(e => e.value).includes(selected)) {
            selected = defaultTab;
        }
        return selected;
    }, [query?.tab, tabsList])

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool
     */
    useEffect(() => {
        if (!defaultCustomer?.code)
            return;
        if (order?.orderId) {
            configureNotebookTags(
                (tags) => tags.customerSalesCreditInformation(defaultCustomer?.code, orderId),
                (tags) => tags.customerSalesInvoiceInformation(defaultCustomer?.code, orderId),
                (tags) => tags.customerSalesSalesOrderInformation(defaultCustomer?.code, order?.orderId),
                (tags) => tags.customerSalesQuoteInformation(defaultCustomer?.code, order?.orderId),
            );
            configureBehavioralTool(tags => tags.customerSalesCreditInformation(defaultCustomer?.code, orderId))
        } else {
            configureNotebookTags(
                (tags) => tags.customerSalesCreditInformation(defaultCustomer?.code, orderId),
                (tags) => tags.customerSalesInvoiceInformation(defaultCustomer?.code, orderId),
            );
            configureBehavioralTool((tags) => tags.customerSalesCreditInformation(defaultCustomer?.code, orderId))
        }
    }, [order?.orderId, orderId, defaultCustomer?.code, configureNotebookTags, configureBehavioralTool]);

    /**
     * Sets the order state with its transactions sorted.
     * @type {function(*=): void}
     */
    const setOrder = useCallback((stateOrFunctionWithPrevState) => {
        _setOrder(prevState => {
            const newState = typeof stateOrFunctionWithPrevState === "function" ? stateOrFunctionWithPrevState(prevState) : stateOrFunctionWithPrevState;
            // sort the appliedTransactions by their type of the id so that the gift card can be at the beginning
            // or at the end.
            newState.appliedTransactions?.sort((a, b) => stringComparator(a.transactionTypeName, b.transactionTypeName));
            return {...newState}
        });
    }, [])

    /**
     * Fetches the customer account information from the server.
     * @type {function(): Promise<void>}
     */
    const getCustomerAccountInformation = useCallback(async () => {
        setAccountInfoLoading(true);
        const [response, priceLevelDict] = await Promise.all([
            Api.getCustomerAccountInformation(customerId),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setAccountInfo({
                ...response.data,
                priceLevel: {
                    ...response.data.priceLevel,
                    title: priceLevelDict?.find(e => e.priceLevelId === response.data.priceLevel.id)?.title ?? response.data.priceLevel.title,
                },
            })
        }
        setAccountInfoLoading(false);
    }, [getPriceLevelDictionary, isMounted, customerId])

    /**
     * As soon as the component is mounted:
     * - fetches user account information from the server.
     */
    useEffect(() => {
        void getCustomerAccountInformation();
    }, [getCustomerAccountInformation])

    /**
     * Fetches the free shipping eligibility of the credit from the server.
     * @type {function(*=): Promise<void>}
     */
    const getFreeShippingEligibility = useCallback(async (freeFreightId = undefined) => {
        if (!operatorAccessLevels.packages.sales.orderFreightInfo) {
            return;
        }
        const response = await Api.getFreeFreightRequest(freeFreightId ?? freeFreightRequestIdRef.current);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setFreeShippingEligibility(response.data);
            setShippingInfo(prevState => ({...prevState, freeFreightId: response.data.id}));
        } else {
            setFreeShippingEligibility({});
        }
    }, [isMounted, operatorAccessLevels.packages.sales.orderFreightInfo])

    /**
     * Fetches the estimated freight fee for the credit based on freight company and related sales order.
     * @type {function(number=): Promise<void>}
     */
    const getFreightEstimation = useCallback(async (freightCompanyId = undefined) => {
        if (!operatorAccessLevels.packages.sales.orderFreightInfo)
            return;
        const resolvedFreightCompanyId = freightCompanyId ?? shippingInfoCompanyIdRef.current;
        const salesOrderId = salesOrderIdRef.current;
        if (!resolvedFreightCompanyId || !salesOrderId) {
            setFreightEstimation(null);
            return;
        }
        setLoadingFreightEstimation(true);
        const response = await Api.estimateShippingFee({
            orderId: salesOrderId,
            freightCompanyId: resolvedFreightCompanyId,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setFreightEstimation(response.data);
        } else {
            setFreightEstimation(null);
        }
        setLoadingFreightEstimation(false);
    }, [isMounted, operatorAccessLevels.packages.sales.orderFreightInfo])

    /**
     * Fetches the credit's shipping information from the server using invoiceId (credit id).
     * @type {function(*=): Promise<void>}
     */
    const getOrderShippingInformation = useCallback(async (orderInfo) => {
        if (!operatorAccessLevels.packages.sales.orderFreightInfo) {
            setLoadingShippingInfo(false);
            return;
        }
        setLoadingShippingInfo(true);
        const response = await Api.getFreightOrderInformation({invoiceId: orderId});
        if (!isMounted())
            return;
        setShippingInfo({
            id: response?.data?.id,
            freeFreightId: response?.data?.freeFreightRequestId,
            freightCompany: {
                id: response?.data?.freightCompanyId,
                title: response?.data?.freightCompanyTitle,
                code: response?.data?.freightCompanyCode ?? orderInfo?.freightCompanyCode
            },
            estimatedDate: response?.data?.estimatedDeliveryDateTime ?? orderInfo?.shipDate,
            estimatedFee: response?.data?.estimatedFee,
            customerFee: response?.data?.customerFee ?? orderInfo?.freightFee,
            trackingNo: response?.data?.trackingNumber ?? orderInfo?.trackingNo,
            shippingBillTo: response?.data?.shippingBillTo ?? null,
            metaData: response?.data?.metaData ?? null,
        });
        freeFreightRequestIdRef.current = response?.data?.freeFreightRequestId;
        shippingInfoCompanyIdRef.current = response?.data?.freightCompanyId;
        salesOrderIdRef.current = orderInfo?.orderId;
        if (response?.resultFlag && response.data?.freightCompanyId && orderInfo?.orderId) {
            await Promise.all([
                getFreeShippingEligibility(response.data.freeFreightRequestId),
                getFreightEstimation(response.data.freightCompanyId),
            ]);
        } else {
            setFreightEstimation(null);
        }
        setLoadingShippingInfo(false);
    }, [orderId, isMounted, getFreeShippingEligibility, getFreightEstimation, operatorAccessLevels.packages.sales.orderFreightInfo])

    /**
     * Fetches the order information from the server.
     * @type {function(boolean=): Promise<void>}
     */
    const getOrderInformation = useCallback(async (setLoadingState = false) => {
        if (setLoadingState) {
            setLoading(true);
        }
        const response = await Api.getCreditInformationForCustomer(customerId, orderId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOrder(response.data ?? {});
        }
        setLoading(false);
        void getOrderShippingInformation(response?.data);
    }, [customerId, orderId, isMounted, setOrder, getOrderShippingInformation])

    /**
     * As soon as the component is mounted:
     * - fetches the order information from the server.
     */
    useEffect(() => {
        void getOrderInformation();
    }, [getOrderInformation]);

    /**
     * Updates the tracking no of the shipping information of this credit.
     * @type {function(*=): Promise<boolean>}
     */
    const updateShippingInfoTrackingNo = useCallback(async (trackingNo) => {
        if (!operatorAccessLevels.packages.sales.manageCredits || !operatorAccessLevels.packages.sales.orderFreightInfo) {
            return;
        }
        const forApi = {
            trackingNo: trackingNo,
            invoiceId: orderId,
            freightInfoId: shippingInfo?.id,
            shippingBillTo: shippingInfo?.shippingBillTo ?? null,
            metaData: shippingInfo?.metaData ?? null,
        }
        const response = await Api.saveInvoiceFreightInfo(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setShippingInfo(prevState => ({
                ...prevState,
                trackingNo: trackingNo,
            }))
        }
        return response?.resultFlag;
    }, [orderId, shippingInfo?.id, shippingInfo?.shippingBillTo, shippingInfo?.metaData, isMounted, operatorAccessLevels.packages.sales.manageCredits, operatorAccessLevels.packages.sales.orderFreightInfo])

    /**
     * Changes the tab of the view.
     * @type {function(*=, *=): void}
     */
    const onTabChange = useCallback((_, newValue) => {
        setQuery(prevState => ({...prevState, tab: newValue}));
    }, [setQuery]);

    const orderInfoSummary = useMemo(() => ({
        header: {
            title: 'Invoice No',
            value: order?.invoiceNo,
            loadingValue: "Loading...",
            copyable: true,
        },
        body: [
            {
                id: orderInfoSummaryBodyIds.transferCode,
                title: 'Transfer code',
                value: order?.transNo,
                copyable: true,
            },
            {
                id: orderInfoSummaryBodyIds.termsCode,
                title: 'Payment Terms',
                value: order?.paymentTermsCode ?? order?.termsCode,
            },
            {
                id: orderInfoSummaryBodyIds.orderNo,
                title: 'Order no',
                value: order?.orderNo,
                copyable: true,
            },
            {
                id: orderInfoSummaryBodyIds.salesPerson,
                title: 'Sales person',
                value: order?.salespersonNo,
            },
            {
                id: orderInfoSummaryBodyIds.accountManager,
                title: 'Account Manager',
                value: order?.accountManagerNo,
            },
            {
                id: orderInfoSummaryBodyIds.territory,
                title: 'Territory',
                value: order?.territory?.code,
            },
            {
                id: orderInfoSummaryBodyIds.orderDate,
                title: 'Order date',
                value: order?.orderDate,
                format: (val) => val ? DateUtils.serverToLocal(val).format(DateUtils.dateFormat) : '--',
            },
            {
                id: orderInfoSummaryBodyIds.requiredDate,
                title: 'Required date',
                value: order?.requiredDate,
                format: (val) => val ? DateUtils.serverToLocal(val).format(DateUtils.dateFormat) : '--',
            },
            {
                id: orderInfoSummaryBodyIds.invoiceDate,
                title: 'Invoice date',
                value: order?.invoiceDate,
                format: (val) => val ? DateUtils.serverToLocal(val).format(DateUtils.dateFormat) : '--',
            },
            {
                id: orderInfoSummaryBodyIds.poNo,
                title: 'Po number',
                value: order?.customerPO,
            },
            {
                id: orderInfoSummaryBodyIds.referenceNo,
                title: 'Reference no',
                value: order?.referenceNo,
            },
        ],
    }), [order])

    const userInfoSummary = useMemo(() => ({
        header: {
            title: 'Customer',
            value: accountInfo?.customer?.name,
            loadingValue: "Loading...",
        },
        body: [
            {
                id: userInfoSummaryBodyIds.accountReceivable,
                title: 'Account receivable',
                value: accountInfo?.accountReceivable,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.limitedCredit,
                title: 'Credit limit',
                value: accountInfo?.creditLimit,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.otherOrders,
                title: 'Other orders',
                value: accountInfo?.otherOrders,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.availableCredit,
                title: 'Available credit',
                value: accountInfo?.availableCredit,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.priceLevel,
                title: 'Price level',
                value: accountInfo?.priceLevel?.title,
            },
            {
                id: userInfoSummaryBodyIds.paymentTerms,
                title: 'Payment terms',
                value: accountInfo?.paymentTermsCode,
            },
        ],
    }), [accountInfo])

    /**
     * Opens the print configuration dialog.
     * @type {(function(): void)}
     */
    const openPrintConfigDialog = useCallback(() => {
        setPrintOrderConfigurationsDialog({
            open: true,
            printUrl: routeFunctions.print.credit(customerId, orderId),
            onClose: () => setPrintOrderConfigurationsDialog(initPrintOrderConfigurationsDialogState)
        })
    }, [customerId, orderId])

    return (
        <>
            <div className='order-information-view sales-views-top-progress-indicator-offset'>
                <Row className='no-gutters w-100 align-items-center justify-content-between'>
                    <Row className='justify-content-start align-items-center no-gutters'>
                        <Tooltip title={`Back to ${defaultCustomer?.name}'s credits`}>
                            <Box component={'span'}>
                                <IconButton
                                    color={'paper.secondary'}
                                    component={Link}
                                    to={routeFunctions.customer.sales.credits.base(customerId)}
                                    replace
                                >
                                    <ArrowBack className='icon normal'/>
                                </IconButton>
                            </Box>
                        </Tooltip>
                        <Typography variant={'h6'} fontWeight={600} marginInlineStart={1}>
                            Credit Information
                        </Typography>
                    </Row>
                    <Row className='justify-content-end align-items-center no-gutters'>
                        {
                            operatorAccessLevels.packages.sales.createOrderFromAnother &&
                            <Tooltip arrow title={'Create Order from this Credit'}>
                                <IconButton
                                    onPressed={() => setDuplicateDialogOpen(true)}
                                    color={'paper.secondary'}
                                >
                                    <FileCopyOutlined className={'icon large'}/>
                                </IconButton>
                            </Tooltip>
                        }
                        {
                            operatorAccessLevels.packages.bizPrint.printCredit &&
                            <Popover
                                child={(onEnter) => (
                                    <Tooltip arrow title={'Print Credit'}>
                                        <IconButton
                                            onPressed={onEnter}
                                            color={'paper.secondary'}
                                        >
                                            <SvgIcon className='icon large' component={Printer}/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                                popoverChild={() => (
                                    <Row
                                        className='no-gutters align-items-center justify-content-center px-2'
                                        style={{maxWidth: '13rem'}}>
                                        <Col xs={12} className={'mx-2 my-2'}>
                                            <Button
                                                color={'secondary'}
                                                variant={'text'}
                                                className={'w-100'}
                                                component={Link}
                                                target={'_blank'}
                                                to={stringifyUrl({
                                                    url: routeFunctions.print.credit(params.customerId, params.orderId),
                                                    query: {showAddresses: true, showPaymentTerms: true, showPrices: true, showTransactions: false}
                                                })}
                                            >
                                                Without transactions
                                            </Button>
                                        </Col>
                                        <Col xs={12} className={'mx-2 mb-2'}>
                                            <Button
                                                color={'secondary'}
                                                variant={'text'}
                                                className={'w-100'}
                                                component={Link}
                                                target={'_blank'}
                                                to={stringifyUrl({
                                                    url: routeFunctions.print.credit(params.customerId, params.orderId),
                                                    query: {showAddresses: true, showPaymentTerms: true, showPrices: true, showTransactions: true}
                                                })}
                                            >
                                                With transactions
                                            </Button>
                                        </Col>
                                        <Col xs={12} className={'mx-2 mb-2'}>
                                            <Button
                                                color={'secondary'}
                                                variant={'text'}
                                                className={'w-100'}
                                                onClick={openPrintConfigDialog}
                                            >
                                                Custom
                                            </Button>
                                        </Col>
                                    </Row>
                                )}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            />
                        }
                    </Row>
                </Row>
                <div className='w-100 my-3'/>
                <div className='d-flex flex-wrap w-100 justify-content-start'>
                    <Col xs={12} md={6} xl={4} className={'mb-3'}>
                        <SummaryInfoCard
                            header={orderInfoSummary.header}
                            body={orderInfoSummary.body}
                            loading={loading}
                            loadingCount={orderInfoSummary.body.length}
                        />
                    </Col>
                    <Col xs={12} md={6} xl={4} className={'mb-3'}>
                        <SummaryInfoCard
                            header={userInfoSummary.header}
                            body={userInfoSummary.body}
                            loading={accountInfoLoading}
                            loadingCount={userInfoSummary.body.length}
                        />
                    </Col>
                    <Col xs={12} md={6} xl={4} className={'mb-3'}>
                        <CreditInformationViewBillInfoSection
                            order={order}
                            loading={loading}
                            customerId={customerId}
                        />
                    </Col>
                    {
                        operatorAccessLevels.packages.sales.orderFreightInfo &&
                        <Col xs={12} md={6} xl={8} className={'mb-3'}>
                            <OrderInformationViewShippingInfoCard
                                viewOnly
                                loading={loading || loadingShippingInfo}
                                freeShippingEligibility={freeShippingEligibility}
                                shippingInfo={shippingInfo}
                                freightEstimation={freightEstimation}
                                loadingFreightEstimation={loadingFreightEstimation}
                                updateTrackingNo={operatorAccessLevels.packages.sales.manageCredits
                                    ? updateShippingInfoTrackingNo
                                    : undefined}
                                getFreeShippingEligibility={getFreeShippingEligibility}
                                getFreightEstimation={getFreightEstimation}
                                customerId={customerId}
                            />
                        </Col>
                    }
                </div>
                <div className='w-100 my-4'/>
                <div className='w-100 flex-grow-1'>
                    <Tabs value={tab} onChange={onTabChange}>
                        {
                            tabsList.map(tab => (
                                <Tab
                                    key={tab.value}
                                    label={tab.label}
                                    value={tab.value}
                                />
                            ))
                        }
                    </Tabs>
                </div>
                <div className='my-3 w-100'/>
                {
                    tab === tabs.products &&
                    <CreditInformationViewProductsSection
                        order={order}
                        orderLoading={loading}
                    />
                }
                {
                    tab === tabs.notes &&
                    <OrderInformationViewNotesSection
                        orderNo={order?.orderNo}
                        loading={loading}
                        placeType={OrderNoteAutoDefineTypes.invoice}
                        editable={operatorAccessLevels.packages.sales.manageCredits}
                    />
                }
                <div className='my-3 w-100'/>
            </div>
            <PrintOrderConfigurationsDialog
                open={printOrderConfigurationsDialog.open}
                onClose={printOrderConfigurationsDialog.onClose}
                printUrl={printOrderConfigurationsDialog.printUrl}
            />
            {
                operatorAccessLevels.packages.sales.createOrderFromAnother &&
                <DuplicateOrderDialog
                    recordId={orderId}
                    customerId={customerId}
                    variant={DuplicateOrderVariants.Credits}
                    open={duplicateDialogOpen}
                    close={() => setDuplicateDialogOpen(false)}
                />
            }
        </>
    );
}
export default CreditInformationView;
